import { BankAccountType } from '@/types/bankAccountType';
import {
  Account,
  calizaWalletUSDC,
  calizaWalletUSDT,
  ContactType,
  FiatAccount,
  revenueEarningsUSDC,
} from '@/types/contacts';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { RecipientResponse } from '@/types/recipient';
import { Transaction, TransactionType } from '@/types/transaction';

export const resolveToAccount = (transaction: Transaction): Account | null => {
  const { type, toAmount, toAccount, beneficiaryName } = transaction;

  if (toAccount) {
    return toAccount;
  }

  switch (type) {
    case TransactionType.DEPOSIT:
    case TransactionType.EXCHANGE:
      if (toAmount.currencyCode === CurrencyCodeType.USDC) {
        return { ...calizaWalletUSDC, name: beneficiaryName ?? calizaWalletUSDC.name };
      } else if (toAmount.currencyCode === CurrencyCodeType.USDT) {
        return { ...calizaWalletUSDT, name: beneficiaryName ?? calizaWalletUSDT.name };
      } else if (toAmount.currencyCode === CurrencyCodeType.USD) {
        return {
          ...heldFundsACHPlaceholderAccount,
          name: beneficiaryName ?? heldFundsACHPlaceholderAccount.name,
        };
      } else {
        return createExternalAccount(transaction);
      }
    case TransactionType.REFUND:
      if (toAmount.currencyCode === CurrencyCodeType.USDC) {
        return { ...calizaWalletUSDC, name: beneficiaryName ?? calizaWalletUSDC.name };
      } else if (toAmount.currencyCode === CurrencyCodeType.USDT) {
        return { ...calizaWalletUSDT, name: beneficiaryName ?? calizaWalletUSDT.name };
      } else if (toAmount.currencyCode === CurrencyCodeType.USD) {
        return {
          ...heldFundsACHPlaceholderAccount,
          name: beneficiaryName ?? heldFundsACHPlaceholderAccount.name,
        };
      } else {
        return null;
      }
    case TransactionType.TRANSFER:
      console.debug('to account is mandatory for transfer transactions');
      return null;
    case TransactionType.INTERNAL_TRANSFER:
      return resolveInternalTransferAccount(transaction, 'to');
    case TransactionType.WITHDRAWAL:
      return createExternalAccount(transaction);
    default:
      console.debug('cannot determine to account for transaction type ' + transaction.type);
      return null;
  }
};

export const resolveFromAccount = (transaction: Transaction): Account | null => {
  const { type, fromAmount, fromAccount, beneficiaryName } = transaction;

  if (fromAccount) {
    return fromAccount;
  }

  switch (type) {
    case TransactionType.WITHDRAWAL:
    case TransactionType.EXCHANGE:
      if (fromAmount.currencyCode === CurrencyCodeType.USDC) {
        return { ...calizaWalletUSDC, name: beneficiaryName ?? calizaWalletUSDC.name };
      } else if (fromAmount.currencyCode === CurrencyCodeType.USDT) {
        return { ...calizaWalletUSDT, name: beneficiaryName ?? calizaWalletUSDT.name };
      } else if (fromAmount.currencyCode === CurrencyCodeType.USD) {
        const account = createHeldFundsAccount(transaction);
        return { ...account, name: beneficiaryName ?? account.name };
      } else {
        const account = createExternalFundingAccount(fromAmount.currencyCode);
        return { ...account, name: beneficiaryName ?? account.name };
      }
    case TransactionType.DEPOSIT: {
      if (fromAmount.currencyCode === CurrencyCodeType.USDC) {
        return {
          ...calizaWalletUSDC,
          id: '',
          name: beneficiaryName ?? calizaWalletUSDC.name,
          chain: externalChainLabel(transaction.paymentType),
        };
      } else if (fromAmount.currencyCode === CurrencyCodeType.USDT) {
        return {
          ...calizaWalletUSDT,
          id: '',
          name: beneficiaryName ?? calizaWalletUSDT.name,
          chain: externalChainLabel(transaction.paymentType),
        };
      }
      const account = createExternalFundingAccount(fromAmount.currencyCode);
      return { ...account, name: beneficiaryName ?? account.name };
    }
    case TransactionType.TRANSFER:
      if (fromAmount.currencyCode === CurrencyCodeType.USDC) {
        return { ...calizaWalletUSDC, name: beneficiaryName ?? calizaWalletUSDC.name };
      } else if (fromAmount.currencyCode === CurrencyCodeType.USDT) {
        return { ...calizaWalletUSDT, name: beneficiaryName ?? calizaWalletUSDT.name };
      } else if (fromAmount.currencyCode === CurrencyCodeType.USD) {
        const account = createHeldFundsAccount(transaction);
        return { ...account, name: beneficiaryName ?? account.name };
      } else {
        const account = createExternalFundingAccount(fromAmount.currencyCode);
        return { ...account, name: beneficiaryName ?? account.name };
      }
    case TransactionType.INTERNAL_TRANSFER:
      return resolveInternalTransferAccount(transaction, 'from');

    default:
      console.error('cannot determine from account for transaction type ' + transaction.type);
      return null;
  }
};

/**
 * INTERNAL_TRANSFER covers two distinct flows:
 *  1. Beneficiary ↔ beneficiary internal transfer — the transaction has a
 *     `targetBeneficiaryId` distinct from `beneficiaryId`. Both sides are Caliza wallets;
 *     each leg is named after the beneficiary that owns it.
 *  2. Revenue earnings withdrawal — no distinct target beneficiary; the
 *     conceptual movement is `Revenue earnings → Caliza wallet` on the integrator's
 *     own beneficiary.
 */
const resolveInternalTransferAccount = (
  transaction: Transaction,
  side: 'from' | 'to',
): Account | null => {
  const {
    fromAmount,
    toAmount,
    beneficiaryId,
    targetBeneficiaryId,
    beneficiaryName,
    targetBeneficiaryName,
  } = transaction;
  const amount = side === 'from' ? fromAmount : toAmount;
  const currency = amount.currencyCode;
  const isBeneficiaryToBeneficiary =
    !!targetBeneficiaryId && !!beneficiaryId && targetBeneficiaryId !== beneficiaryId;

  if (isBeneficiaryToBeneficiary) {
    const ownerName =
      side === 'from' ? beneficiaryName : (targetBeneficiaryName ?? beneficiaryName);

    if (currency === CurrencyCodeType.USDC) {
      return { ...calizaWalletUSDC, name: ownerName ?? calizaWalletUSDC.name };
    }

    if (currency === CurrencyCodeType.USDT) {
      return { ...calizaWalletUSDT, name: ownerName ?? calizaWalletUSDT.name };
    }

    if (currency === CurrencyCodeType.USD) {
      return {
        ...heldFundsACHPlaceholderAccount,
        name: ownerName ?? heldFundsACHPlaceholderAccount.name,
      };
    }

    return null;
  }

  if (currency !== CurrencyCodeType.USDC) {
    return null;
  }

  if (side === 'from') {
    return { ...revenueEarningsUSDC, name: beneficiaryName ?? revenueEarningsUSDC.name };
  }

  return { ...calizaWalletUSDC, name: beneficiaryName ?? calizaWalletUSDC.name };
};

const externalChainLabel = (paymentType: FiatAccountType | PaymentRailType): string => {
  switch (paymentType) {
    case PaymentRailType.Ethereum:
      return 'EXTERNAL_ACCOUNT_ETH';
    case PaymentRailType.TRON:
      return 'EXTERNAL_ACCOUNT_TRON';
    case PaymentRailType.Polygon:
      return 'EXTERNAL_ACCOUNT_POLYGON';
    default:
      return 'EXTERNAL_ACCOUNT';
  }
};

const createExternalFundingAccount = (currencyCode: CurrencyCodeType): Account => {
  switch (currencyCode) {
    case CurrencyCodeType.BRL:
      return {
        ...externalPixPlaceholderAccount,
        accountDetails: { pixDetails: { pixKey: 'EXTERNAL_ACCOUNT_PIX' } },
      };
    case CurrencyCodeType.MXN:
      return {
        ...externalSpeiPlaceholderAccount,
        accountDetails: {
          speiDetails: { clabe: 'EXTERNAL_ACCOUNT_SPEI', bankCode: '', receiverName: '' },
        },
      };
    case CurrencyCodeType.COP:
      return externalCopPlaceholderAccount;
    case CurrencyCodeType.ARS:
      return {
        ...externalCvuPlaceholderAccount,
        accountDetails: { cvuDetails: { cvu: 'EXTERNAL_ACCOUNT_CVU', recipientName: '' } },
      };
    case CurrencyCodeType.EUR:
      return externalSwiftPlaceholderAccount;
    default:
      return externalACHPlaceholderAccount;
  }
};

const createHeldFundsAccount = (transaction: Transaction): Account => {
  switch (transaction.paymentType) {
    case FiatAccountType.wire:
      return heldFundsWirePlaceholderAccount;
    case FiatAccountType.swift:
      return heldFundsSwiftPlaceholderAccount;
    default:
      return heldFundsACHPlaceholderAccount;
  }
};

const createExternalAccount = (transaction: Transaction): Account => {
  switch (transaction.paymentType) {
    case FiatAccountType.pix:
      return externalPixPlaceholderAccount;
    case FiatAccountType.wire:
      return externalWirePlaceholderAccount;
    case FiatAccountType.swift:
      return externalSwiftPlaceholderAccount;
    case FiatAccountType.spei:
      return externalSpeiPlaceholderAccount;
    case FiatAccountType.cop:
      return externalCopPlaceholderAccount;
    case FiatAccountType.cvu:
      return externalCvuPlaceholderAccount;
    default:
      return externalACHPlaceholderAccount;
  }
};

const externalACHPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'EXTERNAL_ACCOUNT_ACH',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.ach,
  nickname: null,
  accountDetails: {
    achDetails: {
      bankInfo: {
        name: 'EXTERNAL_ACCOUNT_ACH',
        street1: '',
        street2: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
        routingNumber: '',
      },
      personInfo: {
        accountType: BankAccountType.checking,
        type: ContactType.individual,
        name: '',
        street1: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
        routingKey: '',
        accountNumber: '',
      },
      businessInfo: null,
    },
  },
};

const externalPixPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'EXTERNAL_ACCOUNT_PIX',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.pix,
  nickname: null,
  accountDetails: {
    pixDetails: {
      pixKey: '',
    },
  },
};

const externalWirePlaceholderAccount: FiatAccount = {
  id: '',
  name: 'EXTERNAL_ACCOUNT_WIRE',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.wire,
  nickname: null,
  accountDetails: {
    wireDetails: {
      personInfo: null,
      businessInfo: null,
      bankInfo: {
        bic: '',
        name: 'EXTERNAL_ACCOUNT_WIRE',
        street1: '',
        street2: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
      },
    },
  },
};

const externalSwiftPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'EXTERNAL_ACCOUNT_SWIFT',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.swift,
  nickname: null,
  accountDetails: {
    swiftDetails: {
      personInfo: null,
      businessInfo: null,
      bankInfo: {
        swiftCode: '',
        name: 'EXTERNAL_ACCOUNT_SWIFT',
        street1: '',
        street2: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
      },
      isPobo: false,
    },
  },
};

const heldFundsACHPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'CALIZA_WALLET',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.ach,
  nickname: null,
  accountDetails: {
    achDetails: {
      bankInfo: {
        name: 'CALIZA_WALLET',
        street1: '',
        street2: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
        routingNumber: '',
      },
      personInfo: {
        accountType: BankAccountType.checking,
        type: ContactType.individual,
        name: '',
        street1: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
        routingKey: '',
        accountNumber: '',
      },
      businessInfo: null,
    },
  },
};

const heldFundsWirePlaceholderAccount: FiatAccount = {
  id: '',
  name: 'CALIZA_WALLET',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.wire,
  nickname: null,
  accountDetails: {
    wireDetails: {
      personInfo: null,
      businessInfo: null,
      bankInfo: {
        bic: '',
        name: 'CALIZA_WALLET',
        street1: '',
        street2: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
      },
    },
  },
};

const heldFundsSwiftPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'CALIZA_WALLET',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.swift,
  nickname: null,
  accountDetails: {
    swiftDetails: {
      personInfo: null,
      businessInfo: null,
      bankInfo: {
        swiftCode: '',
        name: 'CALIZA_WALLET',
        street1: '',
        street2: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
      },
      isPobo: false,
    },
  },
};

const externalSpeiPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'EXTERNAL_ACCOUNT_SPEI',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.spei,
  nickname: null,
  accountDetails: {
    speiDetails: {
      clabe: '',
      bankCode: '',
      receiverName: '',
    },
  },
};

const externalCopPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'EXTERNAL_ACCOUNT_COP',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.cop,
  nickname: null,
  accountDetails: {
    copDetails: {
      firstName: '',
      accountType: 1,
      bankAccount: '',
      bankCode: '',
      thirdPartyWithdrawal: false,
      documentId: null,
      documentType: null,
      email: null,
    },
  },
};

const externalCvuPlaceholderAccount: FiatAccount = {
  id: '',
  name: 'EXTERNAL_ACCOUNT_CVU',
  beneficiaryId: '',
  belongsToBeneficiary: false,
  fiatAccountType: FiatAccountType.cvu,
  nickname: null,
  accountDetails: {
    cvuDetails: {
      cvu: '',
      recipientName: '',
    },
  },
};

const externalACHPlaceholderRecipient: RecipientResponse = {
  id: '',
  currency: CurrencyCodeType.USD,
  type: PaymentRailType.ACH,
  internalTransfer: false,
  belongsToBeneficiary: false,
  individualName: 'EXTERNAL_ACCOUNT_ACH',
  details: { bankName: 'EXTERNAL_ACCOUNT_ACH', accountNumber: '', routingNumber: '' },
};

const externalPixPlaceholderRecipient: RecipientResponse = {
  id: '',
  currency: CurrencyCodeType.BRL,
  type: PaymentRailType.PIX,
  internalTransfer: false,
  belongsToBeneficiary: false,
  individualName: 'EXTERNAL_ACCOUNT_PIX',
  details: { pixKey: '', documentNumber: '' },
};

const externalWirePlaceholderRecipient: RecipientResponse = {
  id: '',
  currency: CurrencyCodeType.USD,
  type: PaymentRailType.Wire,
  internalTransfer: false,
  belongsToBeneficiary: false,
  individualName: 'EXTERNAL_ACCOUNT_WIRE',
  details: { bankName: 'EXTERNAL_ACCOUNT_WIRE', accountNumber: '', routingNumber: '' },
};

const externalSwiftPlaceholderRecipient: RecipientResponse = {
  id: '',
  currency: CurrencyCodeType.USD,
  type: PaymentRailType.Swift,
  internalTransfer: false,
  belongsToBeneficiary: false,
  individualName: 'EXTERNAL_ACCOUNT_SWIFT',
  details: { bankName: 'EXTERNAL_ACCOUNT_SWIFT', swiftCode: '', accountNumber: '' },
};

const externalSpeiPlaceholderRecipient: RecipientResponse = {
  id: '',
  currency: CurrencyCodeType.MXN,
  type: PaymentRailType.SPEI,
  internalTransfer: false,
  belongsToBeneficiary: false,
  individualName: 'EXTERNAL_ACCOUNT_SPEI',
  details: { clabe: '', bankCode: '', receiverName: '' },
};

const externalCopPlaceholderRecipient: RecipientResponse = {
  id: '',
  currency: CurrencyCodeType.COP,
  type: PaymentRailType.COP,
  internalTransfer: false,
  belongsToBeneficiary: false,
  individualName: 'EXTERNAL_ACCOUNT_COP',
  details: { bankCode: '', accountType: '', bankAccount: '' },
};

const externalCvuPlaceholderRecipient: RecipientResponse = {
  id: '',
  currency: CurrencyCodeType.ARS,
  type: PaymentRailType.CVU,
  internalTransfer: false,
  belongsToBeneficiary: false,
  individualName: 'EXTERNAL_ACCOUNT_CVU',
  details: { cvu: '', recipientName: '' },
};

const createExternalRecipient = (transaction: Transaction): RecipientResponse => {
  switch (transaction.paymentType) {
    case FiatAccountType.pix:
      return externalPixPlaceholderRecipient;
    case FiatAccountType.wire:
      return externalWirePlaceholderRecipient;
    case FiatAccountType.swift:
      return externalSwiftPlaceholderRecipient;
    case FiatAccountType.spei:
      return externalSpeiPlaceholderRecipient;
    case FiatAccountType.cop:
      return externalCopPlaceholderRecipient;
    case FiatAccountType.cvu:
      return externalCvuPlaceholderRecipient;
    default:
      return externalACHPlaceholderRecipient;
  }
};

export const resolveToRecipient = (transaction: Transaction): RecipientResponse | null => {
  if (transaction.toRecipient) {
    return transaction.toRecipient;
  }

  // For legacy transactions without toRecipient, return placeholder
  const { type, toAmount } = transaction;
  switch (type) {
    case TransactionType.DEPOSIT:
    case TransactionType.EXCHANGE:
      if (
        toAmount.currencyCode === CurrencyCodeType.USDC ||
        toAmount.currencyCode === CurrencyCodeType.USDT ||
        toAmount.currencyCode === CurrencyCodeType.USD
      ) {
        return null; // caliza wallet, not a recipient
      }
      return createExternalRecipient(transaction);
    case TransactionType.WITHDRAWAL:
      return createExternalRecipient(transaction);
    case TransactionType.TRANSFER:
      console.debug('to recipient is mandatory for transfer transactions');
      return null;
    default:
      return null;
  }
};
